import { extends as _extends, objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useTabsContext } from './tabs-context.esm.js';
import { TAB_THEME_KEY } from './types.esm.js';
import { isNotEmptyObject } from '../../utils/assertion.esm.js';
import { omit, pick, EMPTY_OBJECT } from '../../utils/object.esm.js';
import { getLayout, getPaddingY, getPaddingX, omitPaddingX, omitPaddingY, omitLayout } from '../../utils/props-utils.esm.js';
import Box from '../_primitives/box/box.esm.js';
import { ButtonPrivate } from '../button/button.esm.js';

var _excluded = ["value", "label", "selected", "disabled", "variant", "className", "onChange", "onClick", "onFocus", "selectionFollowsFocus", "orientation", "indicatorPosition", "iconBefore", "iconAfter", "sx"];
var TabPrivate = /*#__PURE__*/React.forwardRef(function TabPrivate(props, ref) {
  var value = props.value,
    label = props.label,
    selected = props.selected,
    disabled = props.disabled,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'tab' : _props$variant,
    className = props.className,
    onChange = props.onChange,
    onClick = props.onClick,
    onFocus = props.onFocus,
    selectionFollowsFocus = props.selectionFollowsFocus,
    orientation = props.orientation,
    indicatorPosition = props.indicatorPosition,
    iconBefore = props.iconBefore,
    iconAfter = props.iconAfter,
    _props$sx = props.sx,
    sx = _props$sx === void 0 ? EMPTY_OBJECT : _props$sx,
    rest = _objectWithoutProperties(props, _excluded);
  var _useTabsContext = useTabsContext(),
    tabVariant = _useTabsContext.variant;

  // remove size from layout fields
  var combineLayout = omit(_objectSpread2(_objectSpread2(_objectSpread2({}, orientation === 'vertical' ? {} : {
    height: '100%'
  }), getLayout(rest)), getLayout(sx)), ['size']);
  var combinePadding = _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, getPaddingX(rest)), getPaddingX(sx)), getPaddingY(rest)), getPaddingY(sx));
  var paddingProps = isNotEmptyObject(combinePadding) ? combinePadding : _objectSpread2({}, orientation === 'horizontal' ? {
    emPaddingX: 0
  } : {
    emPaddingY: 0
  });
  var handleClick = useCallback(function (event) {
    if (onChange && value !== undefined) {
      onChange(event, {
        value: value,
        label: label,
        icon: iconBefore
      });
    }
    if (onClick) {
      onClick(event);
    }
  }, [label, onChange, onClick, value, iconBefore]);
  var handleFocus = useCallback(function (event) {
    if (selectionFollowsFocus && !selected && onChange && value !== undefined) {
      onChange(event, {
        value: value,
        label: label,
        icon: iconBefore
      });
    }
    if (onFocus) {
      onFocus(event);
    }
  }, [onFocus, onChange, selectionFollowsFocus, selected, value, label, iconBefore]);

  // eslint-disable-next-line no-console
  // console.log(`tab render value ${value}`)

  var tab = /*#__PURE__*/React.createElement(ButtonPrivate, _extends({
    ref: ref,
    role: "tab",
    variant: tabVariant ? "".concat(tabVariant, ".").concat(variant) : variant,
    __themeKey: TAB_THEME_KEY,
    className: classNames({
      'csg-tui-tab': true,
      'is-selected': selected,
      'is-disabled': disabled
    }, className),
    iconAfter: iconAfter,
    "aria-selected": selected,
    onClick: !disabled ? handleClick : undefined,
    onFocus: !disabled ? handleFocus : undefined,
    disabled: disabled,
    tabIndex: selected && !disabled ? 0 : -1,
    iconBefore: iconBefore,
    __css: _objectSpread2(_objectSpread2({}, paddingProps), combineLayout),
    sx: omitPaddingX(omitPaddingY(omitLayout(sx)))
  }, _objectSpread2(_objectSpread2(_objectSpread2({}, omitPaddingX(omitPaddingY(omitLayout(rest)))), pick(rest, ['size'])), 'size' in rest || 'fontSize' in rest ? {} : {
    fontSize: '100%'
  })));
  return orientation === 'vertical' ? /*#__PURE__*/React.createElement(Box, {
    className: "csg-tui-tab-wrapper",
    emPaddingRight: "xxl",
    emPaddingLeft: indicatorPosition === 'left' ? 'lg' : 'md'
  }, tab) : tab;
});
var Tab = /*#__PURE__*/React.forwardRef(function Tab(props, ref) {
  return /*#__PURE__*/React.createElement(TabPrivate, _extends({
    ref: ref
  }, props));
});

export default Tab;
export { TabPrivate };
//# sourceMappingURL=tab.esm.js.map
